import { Link as RouterLink } from 'react-router-dom';
import { useLoginForm, type LoginTemplateProps } from './useLoginForm';
import LoginOtpPanel from './LoginOtpPanel';

/**
 * The Journey sign-in card — email, password, Remember me. Class names follow the supplied design
 * (styles in pages/auth/tripzaLogin.css); the behaviour is useLoginForm's, shared by every design.
 */
export default function LoginCard({ brand = null, initialEmail = '', preview = false }: Partial<LoginTemplateProps>) {
  const { form, onSubmit, notice, error, busy, showPwd, toggleShowPwd, remember, setRemember, otp } =
    useLoginForm(initialEmail, preview);
  const { register, formState: { errors } } = form;

  return (
    <section className="panel">
      <div className="card">
        {brand?.logoUrl
          ? <img src={brand.logoUrl} alt={brand.companyName} style={{ display: 'block', maxWidth: 160, maxHeight: 56, objectFit: 'contain', margin: '0 auto 14px' }} />
          : <div className="avatar">✈️</div>}
        <h2>Welcome back 👋</h2>
        <p className="sub">{brand ? `Sign in to ${brand.companyName}.` : 'Sign in to your account.'}</p>

        {notice && !error && <div className="formmsg show info">{notice}</div>}

        {otp.step ? <LoginOtpPanel otp={otp} /> : (
        <form onSubmit={onSubmit} noValidate>
          <div className={`field${errors.email ? ' invalid' : ''}`}>
            <label htmlFor="email">Email</label>
            <div className="inp">
              <span className="ic">✉️</span>
              <input type="email" id="email" placeholder="you@company.com" autoComplete="username" {...register('email')} />
            </div>
            <div className="err">Enter a valid email address.</div>
          </div>

          <div className={`field${errors.password ? ' invalid' : ''}`}>
            <label htmlFor="password">Password</label>
            <div className="inp">
              <span className="ic">🔒</span>
              <input
                type={showPwd ? 'text' : 'password'} id="password" placeholder="••••••••"
                autoComplete="current-password"
                {...register('password')}
              />
              <button type="button" className="toggle" onClick={toggleShowPwd}>
                {showPwd ? 'Hide' : 'Show'}
              </button>
            </div>
            <div className="err">Enter your password.</div>
          </div>

          <div className="row">
            <label className="remember">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Remember me
            </label>
            <RouterLink to="/forgot-password" className="fp">Forgot password?</RouterLink>
          </div>

          <button type="submit" className={`submit${busy ? ' loading' : ''}`} disabled={busy || preview}>
            <span className="spin" />
            <span className="lbl">Sign in →</span>
          </button>

          {error && <div className="formmsg show bad" role="alert">{error}</div>}
        </form>
        )}

        {/* Joining the product is offered only on TravelERP's own address. */}
        {!brand && (
          <p className="contact">
            New here? <RouterLink to="/onboarding">Start your free trial</RouterLink>
          </p>
        )}
        <p className="contact">Team members: use the invitation email from your admin.</p>
      </div>
    </section>
  );
}
